import React, { useCallback } from 'react';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, fade, useTheme } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InsertPhotoIcon from '@material-ui/icons/InsertPhoto';
import CloseIcon from '@material-ui/icons/Close';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import withFormField from '../../hocs/withFormField';
import ImageIcon from '../../assets/ImageIcon';

const useStyles = makeStyles((theme) => ({
  message: {
    marginTop: 40,
    fontWeight: 'bold',
    fontSize: 'inherit',
    color: theme.palette.common.black,
  },
  uploadIcon: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontSize: 22,
  },
  dropZone: {
    borderRadius: 15,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 250,
    backgroundColor: theme.palette.white,
    textAlign: 'center',
    padding: theme.spacing(3),
    transition: 'background-color 0.5s, border-color 0.5s',
    backgroundImage: "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='15' ry='15' stroke='%23768F77FF' stroke-width='4' stroke-dasharray='2%2c 4' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e\")",
    '& button': {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      border: 'none',
      background: 'inherit',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      fontWeight: 'inherit',
      padding: 0,
      '&:focus': {
        outline: 'none',
      },
    },
    '&:focus': {
      outline: 'none',
    },
    '&$disabled': {
      color: theme.palette.darkGrey.light,
      borderColor: fade(theme.palette.darkGrey.light, 0.5),
      '& button, & $uploadIcon': {
        color: fade(theme.palette.darkGrey.light, 0.5),
      },
    },
  },
  dragging: {
    backgroundColor: fade(theme.palette.lightGrey.main, 0.8),
    borderColor: fade(theme.palette.primary.light, 0.8),
  },
  disabled: {},
  uploaded: {
    backgroundColor: fade(theme.palette.primary.light, 0.2),
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  uploadedIcon: {
    color: theme.palette.secondary.main,
  },
  fileName: {
    width: '100%',
    margin: theme.spacing(0, 2),
  },
  removeIconButton: {
    padding: 0,
  },
}));

const FileInput = ({
  handleChange, info, value: files, disabled, accept, multiple, type,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (!!rejectedFiles && !!rejectedFiles.length) console.error('Rejected:', rejectedFiles); // eslint-disable-line no-console
    if (acceptedFiles.length && multiple) handleChange([...files, ...acceptedFiles]);
    else if (acceptedFiles.length) handleChange([...acceptedFiles]);
  }, [files, handleChange]);
  const removeFile = useCallback((index) => () => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    handleChange(newFiles);
  }, [files, handleChange]);

  let accetpFileTypes = accept;

  if (type === 'image') if (typeof accept === 'string') accetpFileTypes = `${accept}, .heic, .heif, image/*`;
  else if (Array.isArray(accept)) accetpFileTypes = ['.heic', '.heif', 'image/*', ...accept];
  else accetpFileTypes = '.heic, .heif, image/*';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled,
    accept: accetpFileTypes,
    multiple,
    onDrop,
  });

  const hasSelectedFiles = !!files && !!files.length;

  return (
    <div>
      {hasSelectedFiles && files.map((file, index) => (
        <div
          className={classes.uploaded}
          key={`${file.name}${file.lastModified}`}
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onKeyDown={(e) => e.stopPropagation()}
          role="button"
          tabIndex={0}
        >
          {type === 'image' ? (
            <InsertPhotoIcon className={classes.uploadedIcon} />
          ) : (
            <CloudUploadIcon className={classes.uploadedIcon} />
          )}

          <Typography className={classes.fileName}>
            {file.name}
          </Typography>
          <IconButton
            onClick={removeFile(index)}
            size="small"
            color="inherit"
            aria-label="remove"
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </div>
      ))}
      <div
        {...getRootProps({
          className: clsx(
            classes.dropZone,
            isDragActive && classes.dragging,
            disabled && classes.disabled
          ),
        })}
      >
        <input {...getInputProps()} />
        {type === 'image' ? (
          <ImageIcon className={classes.uploadIcon} />
        ) : (
          <CloudUploadIcon className={classes.uploadIcon} />
        )}

        <Typography className={classes.message} variant="body1">
          {type === 'image' ? (
            <Trans i18nKey="buttons:dragImageOrExplore">
              Drag your image or <button type="button" style={{ color: theme.palette.secondary.main }}>explore</button>
            </Trans>
          ) : (
            <Trans i18nKey="buttons:dragFileOrExplore">
              Drag your file or <button type="button" style={{ color: theme.palette.secondary.main }}>explore</button>
            </Trans>
          )}
        </Typography>
        {info}
      </div>
    </div>
  );
};

FileInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  handleChange: PropTypes.func.isRequired,
  info: PropTypes.node,
  accept: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['file', 'image']),
};

FileInput.defaultProps = {
  multiple: false,
  accept: undefined,
  info: undefined,
  disabled: undefined,
  type: 'file',
};

export default withFormField({})(FileInput);
